import React, { useState } from 'react';
import Icon from 'components/AppIcon';

const DocumentPanel = ({ caseNumber }) => {
  const [selectedFolder, setSelectedFolder] = useState('case-documents');
  const [searchTerm, setSearchTerm] = useState('');

  const folders = [
    { id: 'case-documents', name: 'Case Documents', count: 12 },
    { id: 'contracts', name: 'Contracts', count: 5 },
    { id: 'evidence', name: 'Evidence', count: 8 },
    { id: 'correspondence', name: 'Correspondence', count: 15 },
    { id: 'shared-today', name: 'Shared Today', count: 3 }
  ];

  const documents = [
    {
      id: 1,
      name: 'Employment_Contract_Draft_v2.pdf',
      type: 'pdf',
      size: '2.4 MB',
      lastModified: '10 minutes ago',
      sharedInCall: true,
      folder: 'case-documents'
    },
    {
      id: 2,
      name: 'Legal_Precedent_Research.docx',
      type: 'docx',
      size: '856 KB',
      lastModified: '1 hour ago',
      sharedInCall: false,
      folder: 'case-documents'
    },
    {
      id: 3,
      name: 'Client_Statement_Signed.pdf',
      type: 'pdf',
      size: '1.2 MB',
      lastModified: '2 days ago',
      sharedInCall: true,
      folder: 'evidence'
    },
    {
      id: 4,
      name: 'Settlement_Proposal.pdf',
      type: 'pdf',
      size: '678 KB',
      lastModified: '3 days ago',
      sharedInCall: false,
      folder: 'case-documents'
    }
  ];

  const getFileIcon = (type) => {
    switch (type.toLowerCase()) {
      case 'pdf': return 'FileText';
      case 'docx': case'doc': return 'FileText';
      case 'xlsx': case'xls': return 'FileSpreadsheet';
      case 'jpg': case'jpeg': case'png': return 'Image';
      default: return 'File';
    }
  };

  const getFileTypeColor = (type) => {
    switch (type.toLowerCase()) {
      case 'pdf': return 'text-error';
      case 'docx': case'doc': return 'text-primary';
      case 'xlsx': case'xls': return 'text-success';
      case 'jpg': case'jpeg': case'png': return 'text-warning-600';
      default: return 'text-text-secondary';
    }
  };

  const filteredDocuments = documents.filter(doc => 
    (selectedFolder === 'shared-today' ? doc.sharedInCall : doc.folder === selectedFolder) &&
    doc.name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const shareDocument = (docId) => {
    // In a real implementation, this would share the document in the video call
    alert('Document shared with client in video call');
  };

  const previewDocument = (docId) => {
    // In a real implementation, this would open a document preview
    alert('Document preview would open here');
  };

  return (
    <div className="flex flex-col h-full">
      {/* Documents Header */}
      <div className="p-4 border-b border-border">
        <div className="flex items-center justify-between mb-3">
          <h3 className="font-medium text-text-primary">Case Documents</h3>
          <button className="p-2 text-text-secondary hover:text-primary transition-smooth">
            <Icon name="Upload" size={16} />
          </button>
        </div>
        
        {/* Search */}
        <div className="relative">
          <Icon 
            name="Search" 
            size={16} 
            className="absolute left-3 top-1/2 transform -translate-y-1/2 text-text-secondary"
          />
          <input
            type="text"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            placeholder="Search documents..."
            className="w-full pl-10 pr-3 py-2 text-sm border border-border rounded-interactive focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
          />
        </div>
      </div>

      {/* Folder Navigation */}
      <div className="px-4 py-3 border-b border-border">
        <div className="space-y-1">
          {folders.map((folder) => (
            <button
              key={folder.id}
              onClick={() => setSelectedFolder(folder.id)}
              className={`w-full flex items-center justify-between px-3 py-2 rounded-interactive text-sm transition-smooth ${
                selectedFolder === folder.id
                  ? 'bg-primary-50 text-primary' :'text-text-secondary hover:text-text-primary hover:bg-secondary-50'
              }`}
            >
              <div className="flex items-center space-x-2">
                <Icon name="Folder" size={14} />
                <span>{folder.name}</span>
              </div>
              <span className="text-xs">{folder.count}</span>
            </button>
          ))}
        </div>
      </div>

      {/* Documents List */}
      <div className="flex-1 overflow-y-auto">
        {filteredDocuments.length > 0 ? (
          <div className="p-4 space-y-3">
            {filteredDocuments.map((doc) => (
              <div
                key={doc.id}
                className="flex items-start space-x-3 p-3 border border-border rounded-lg hover:bg-secondary-50 transition-smooth group"
              >
                <Icon 
                  name={getFileIcon(doc.type)} 
                  size={20} 
                  className={`flex-shrink-0 mt-0.5 ${getFileTypeColor(doc.type)}`}
                />
                
                <div className="flex-1 min-w-0">
                  <div className="flex items-start justify-between">
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-text-primary truncate">
                        {doc.name}
                      </p>
                      <div className="flex items-center space-x-3 mt-1 text-xs text-text-secondary">
                        <span>{doc.size}</span>
                        <span>•</span>
                        <span>{doc.lastModified}</span>
                        {doc.sharedInCall && (
                          <>
                            <span>•</span>
                            <div className="flex items-center space-x-1 text-primary">
                              <Icon name="Share" size={10} />
                              <span>Shared</span>
                            </div>
                          </>
                        )}
                      </div>
                    </div>
                    
                    <div className="flex items-center space-x-1 opacity-0 group-hover:opacity-100 transition-smooth">
                      <button
                        onClick={() => previewDocument(doc.id)}
                        className="p-1 text-text-secondary hover:text-primary transition-smooth"
                        title="Preview Document"
                      >
                        <Icon name="Eye" size={14} />
                      </button>
                      
                      {!doc.sharedInCall && (
                        <button
                          onClick={() => shareDocument(doc.id)}
                          className="p-1 text-text-secondary hover:text-primary transition-smooth"
                          title="Share in Video Call"
                        >
                          <Icon name="Share" size={14} />
                        </button>
                      )}
                      
                      <button
                        className="p-1 text-text-secondary hover:text-primary transition-smooth"
                        title="More Options"
                      >
                        <Icon name="MoreHorizontal" size={14} />
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="flex flex-col items-center justify-center h-64 text-text-secondary">
            <Icon name="FolderOpen" size={40} className="mb-3" />
            <p className="text-sm">
              {searchTerm ? 'No documents match your search' : 'No documents in this folder'}
            </p>
          </div>
        )}
      </div>

      {/* Quick Actions */}
      <div className="p-4 border-t border-border">
        <div className="grid grid-cols-2 gap-2">
          <button className="flex items-center justify-center space-x-2 px-3 py-2 text-sm text-text-secondary hover:text-primary hover:bg-secondary-50 border border-border rounded-interactive transition-smooth">
            <Icon name="Upload" size={14} />
            <span>Upload</span>
          </button>
          
          <button className="flex items-center justify-center space-x-2 px-3 py-2 text-sm text-text-secondary hover:text-primary hover:bg-secondary-50 border border-border rounded-interactive transition-smooth">
            <Icon name="Share" size={14} />
            <span>Share Screen</span>
          </button>
        </div>
        
        <div className="mt-2 text-xs text-text-secondary text-center">
          All documents are encrypted and secure
        </div>
      </div>
    </div>
  );
};

export default DocumentPanel;